import { HttpErrorResponse } from '@angular/common/http';
import {
  computed,
  DOCUMENT,
  effect,
  inject,
  linkedSignal,
  Service,
  signal,
  untracked,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import type Fuse from 'fuse.js';
import type { FuseResult } from 'fuse.js';
import { MenuCategory, MenuItem, Restaurant, WifiInfo } from '../../data/models';
import { QrService } from '../../data/qr.service';
import { mapMenuToCategories, mapMenuToItems, mapOrgToRestaurant } from '../../data/qr.mapper';
import { QrMenuResponse, QrOrgResponse } from '../../data/qr.types';
import { QrTokenService } from '../../core/qr-token.service';
import { LanguageService } from '../../core/language.service';
import { countKey } from '../../core/i18n';
import {
  coerceTemplate,
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_TEMPLATE,
  DEFAULT_TEXT_SIZE,
  STORAGE_KEYS,
  TemplateKey,
} from '../../core/constants';
import { generatePalette } from '../../shared/color-contrast';
import {
  canonicalize,
  CanonicalText,
  HighlightRange,
  toOriginalRange,
} from '../../shared/search-text';
import { itemPriceView } from './item-pricing';
import { Layout, MenuEntry, Tab, TabId } from './menu.types';
import { PromoStore } from './promo-store';

/** Untouched backend payload; the language-dependent mapping happens downstream in `data`. */
type RawState =
  | { kind: 'loading' }
  | { kind: 'loaded'; org: QrOrgResponse; menu: QrMenuResponse }
  | { kind: 'notFound' }
  | { kind: 'error' };

type LoadState =
  | { kind: 'loading' }
  | {
      kind: 'loaded';
      restaurant: Restaurant;
      categories: MenuCategory[];
      items: MenuItem[];
    }
  | { kind: 'notFound' }
  | { kind: 'error' };

/** Fuse score cutoff: 0 = exact only, 1 = anything. The 0.6 default matches far too loosely. */
const FUZZY_THRESHOLD = 0.34;
/** Below this many canonical chars, fuzzy matching is pure noise — substring search covers it. */
const MIN_FUZZY_QUERY = 2;
/** Rank offset keeping every name match ahead of any description match in the fallback sort. */
const DESCRIPTION_RANK_OFFSET = 1_000_000;
/** Preload delay when requestIdleCallback is unavailable (Safari). */
const IDLE_FALLBACK_MS = 2000;

const NO_RANGES: readonly HighlightRange[] = [];

interface SearchEntry {
  item: MenuItem;
  name: CanonicalText;
  description: CanonicalText;
}

function readStoredLayout(): Layout {
  try {
    const value = sessionStorage.getItem(STORAGE_KEYS.LAYOUT);
    return value === 'grid' || value === 'list' ? value : 'list';
  } catch {
    // sessionStorage unavailable (private mode / blocked) — fall back to the default.
    return 'list';
  }
}

/** Loads the menu (retryable on failure) and holds all page state: tabs, category filter, layout. */
@Service({ autoProvided: false })
export class MenuState {
  private qr = inject(QrService);
  private qrToken = inject(QrTokenService);
  private doc = inject(DOCUMENT);
  private language = inject(LanguageService);
  private lang = this.language.lang;
  private promo = inject(PromoStore);

  /**
   * Dev/preview override: `?template=<key>` forces a theme regardless of the backend value
   * (validated by the same whitelist; garbage is ignored). Read once — the URL is stable for
   * the session. `null` when absent/invalid, so the backend value wins.
   */
  private readonly templateOverride: TemplateKey | null = coerceTemplate(
    new URLSearchParams(this.doc.defaultView?.location.search ?? '').get('template'),
  );

  /** No `params`: the load starts once and re-runs only via retry() -> reload(). */
  private readonly menuRes = rxResource<RawState, void>({
    stream: () => this.loadMenu(),
    defaultValue: { kind: 'loading' },
  });

  // 'reloading' keeps the previous value; fold it back to 'loading' so a retry shows the
  // skeleton (not the stale error) exactly like the old startWith-based pipeline did.
  private raw = computed<RawState>(() =>
    this.menuRes.isLoading() ? { kind: 'loading' } : this.menuRes.value(),
  );

  /**
   * Localized menu: the raw payload mapped through the active language. Everything downstream
   * (tabs, search index, the grid) is derived from here, so switching the language rebuilds the
   * whole menu without a refetch.
   */
  private data = computed<LoadState>(() => {
    const r = this.raw();
    if (r.kind !== 'loaded') return r;
    const lang = this.lang();
    return {
      kind: 'loaded',
      restaurant: mapOrgToRestaurant(r.org),
      categories: mapMenuToCategories(r.menu, lang),
      items: mapMenuToItems(r.menu, lang),
    };
  });

  readonly isLoading = computed(() => this.data().kind === 'loading');
  readonly isNotFound = computed(() => this.data().kind === 'notFound');
  readonly isError = computed(() => this.data().kind === 'error');
  readonly loaded = computed(() => {
    const s = this.data();
    return s.kind === 'loaded' ? s : null;
  });
  /** Boolean gate for effects that must not refire on a language remap (loaded() changes identity). */
  readonly hasMenu = computed(() => this.raw().kind === 'loaded');

  // linkedSignal resets the selection to 'all' whenever a new menu loads. Sourced from the RAW
  // load, not `loaded`: a language switch remaps the menu but must keep the guest's category.
  readonly selectedCategoryId = linkedSignal<unknown, TabId>({
    source: this.raw,
    computation: () => 'all',
  });
  readonly layout = signal<Layout>(readStoredLayout());

  /** При defaultPromo скидка на всём меню — вкладка дублировала бы «Все позиции», поэтому пусто. */
  private readonly promoItems = computed<MenuItem[]>(() => {
    const l = this.loaded();
    const promos = this.promo.promos();
    if (!l || promos.defaultPercent !== null) return [];
    return l.items.filter((i) => itemPriceView(i, promos).percent > 0);
  });

  readonly tabs = computed<Tab[]>(() => {
    const l = this.loaded();
    if (!l) return [];
    return [
      { id: 'all', name: this.t('menu.allTab') },
      ...(this.promoItems().length > 0
        ? [{ id: 'promo' as const, name: this.t('menu.promoTab') }]
        : []),
      ...l.categories.map((c) => ({ id: c.id, name: c.name })),
    ];
  });

  readonly filteredItems = computed<MenuItem[]>(() => {
    const l = this.loaded();
    if (!l) return [];
    const sel = this.selectedCategoryId();
    if (sel === 'all') return l.items;
    if (sel === 'promo') return this.promoItems();
    return l.items.filter((i) => i.categoryId === sel);
  });

  /** Live, client-side search text. Empty string = not searching (local filter, never an API call). */
  readonly query = signal('');
  private readonly canonicalQuery = computed(() => canonicalize(this.query().trim()).text);
  readonly isSearching = computed(() => this.canonicalQuery().length > 0);

  /** The Fuse constructor once its lazy chunk arrives; null → substring fallback. */
  private readonly fuseCtor = signal<typeof Fuse | null>(null);
  private fuseLoading = false;

  private readonly searchEntries = computed<SearchEntry[]>(() => {
    const l = this.loaded();
    if (!l) return [];
    // Backend can send a null name/description despite the typed `string`; guard before folding.
    return l.items.map((item) => ({
      item,
      name: canonicalize(item.name ?? ''),
      description: canonicalize(item.description ?? ''),
    }));
  });

  private readonly fuseIndex = computed<Fuse<SearchEntry> | null>(() => {
    const FuseCtor = this.fuseCtor();
    const entries = this.searchEntries();
    if (!FuseCtor || entries.length === 0) return null;
    return new FuseCtor(entries, {
      keys: [
        { name: 'name', getFn: (e: SearchEntry) => e.name.text, weight: 0.7 },
        { name: 'description', getFn: (e: SearchEntry) => e.description.text, weight: 0.3 },
      ],
      threshold: FUZZY_THRESHOLD,
      ignoreLocation: true,
      minMatchCharLength: MIN_FUZZY_QUERY,
      includeScore: true,
      includeMatches: true,
    });
  });

  /**
   * Search hits across the whole menu (ANY category), best matches first, with match spans
   * for highlighting. Fuzzy via fuse.js once its chunk is in; plain substring until then.
   */
  readonly searchHits = computed<MenuEntry[]>(() => {
    const q = this.canonicalQuery();
    if (!q || !this.loaded()) return [];
    // untracked: the fuse chunk landing MID-query must not reshuffle results the guest is
    // already looking at (or re-fire the aria-live count) — the next keystroke picks it up.
    const fuse = untracked(this.fuseIndex);
    if (fuse && q.length >= MIN_FUZZY_QUERY) return this.fuzzySearch(fuse, q);
    const hits = this.substringSearch(q);
    if (hits.length === 0 && q.length >= MIN_FUZZY_QUERY) {
      // A dead end has nothing to disturb — here the chunk's arrival may upgrade the
      // results on its own (tracked read).
      const lateFuse = this.fuseIndex();
      if (lateFuse) return this.fuzzySearch(lateFuse, q);
    }
    return hits;
  });

  /** What the grid renders: search hits across the whole menu while searching, else the category filter. */
  readonly displayedItems = computed<MenuEntry[]>(() =>
    this.isSearching()
      ? this.searchHits()
      : this.filteredItems().map((item) => ({
          item,
          nameRanges: NO_RANGES,
          descriptionRanges: NO_RANGES,
        })),
  );

  /** Heading above the grid: «Результаты» while searching, else the category name. */
  readonly displayHeading = computed(() =>
    this.isSearching() ? this.t('menu.searchHeading') : this.currentCategoryName(),
  );

  /** Count line under the heading: pluralized matches while searching, item count while browsing. */
  readonly countLabel = computed(() => {
    const n = this.displayedItems().length;
    const searching = this.isSearching();
    if (searching && n === 0) return this.t('menu.noResults');
    return `${n} ${this.t(countKey(searching ? 'menu.results' : 'menu.positions', n, this.lang()))}`;
  });

  /** Wi-Fi card data; present only when the backend sent both name and password. */
  readonly wifi = computed<WifiInfo | null>(() => {
    const restaurant = this.loaded()?.restaurant;
    if (!restaurant?.wifiName || !restaurant.wifiPassword) return null;
    return { name: restaurant.wifiName, password: restaurant.wifiPassword };
  });

  /** Brand accent color for the --brand-primary host CSS variable. */
  readonly brandColor = computed(() => this.loaded()?.restaurant.primaryColor ?? null);

  /**
   * Active visual template. The `?template=` override wins when present (preview); otherwise the
   * backend value once loaded; `default` until then and on error (never a themed skeleton).
   */
  readonly designTemplate = computed<TemplateKey>(
    () => this.templateOverride ?? this.loaded()?.restaurant.designTemplate ?? DEFAULT_TEMPLATE,
  );

  /** Admin-set base font size (px); drives the root font-size scale. Defaults to the design baseline. */
  readonly baseFontSize = computed(() => this.loaded()?.restaurant.textSize ?? DEFAULT_TEXT_SIZE);

  /**
   * Full brand palette derived from the single admin-set primary color: the contrast-safe
   * fill + text for the primary button, and the brand-tinted onyx for the secondary. The
   * restaurant picks one color; every token is resolved to WCAG AA.
   */
  readonly palette = computed(() =>
    generatePalette(this.brandColor() ?? DEFAULT_PRIMARY_COLOR),
  );

  /** A table QR was scanned — required for ANY table-service action (call waiter, bill, orders). */
  readonly hasTable = computed(() => this.qrToken.tableUuid() !== null);

  // Both gate on hasTable: without a table the actions can't be routed, so hide them
  // even when the venue enabled the flags.
  readonly showCallWaiter = computed(
    () => this.hasTable() && (this.loaded()?.restaurant.actionBar.callWaiter ?? false),
  );
  readonly showBill = computed(
    () => this.hasTable() && (this.loaded()?.restaurant.actionBar.bill ?? false),
  );

  /** Whether the venue enabled ordering (the qrOrdering flag); gates cart controls. */
  readonly ordering = computed(() => this.loaded()?.restaurant.ordering ?? false);

  /** Whether a guest may open a new order when the table has none (the qrOrderCreate flag). */
  readonly orderCreate = computed(() => this.loaded()?.restaurant.orderCreate ?? false);

  readonly phone = computed(() => this.loaded()?.restaurant.phone ?? null);
  readonly address = computed(() => this.loaded()?.restaurant.address ?? null);
  readonly socials = computed(() => this.loaded()?.restaurant.socials ?? []);

  readonly currentCategoryName = computed(() => {
    const sel = this.selectedCategoryId();
    return this.tabs().find((t) => t.id === sel)?.name ?? this.t('menu.defaultTitle');
  });

  private t(key: string): string {
    return this.language.t(key);
  }

  constructor() {
    effect(() => {
      const l = this.layout();
      try {
        sessionStorage.setItem(STORAGE_KEYS.LAYOUT, l);
      } catch {
        // Storage unavailable — the layout still lives in the signal for this session.
      }
    });
    // Preload the fuzzy-search chunk once the menu is on screen, off the critical path.
    effect(() => {
      if (!this.hasMenu()) return;
      const win = this.doc.defaultView;
      if (!win) return;
      if (typeof win.requestIdleCallback === 'function') {
        win.requestIdleCallback(() => this.loadFuse());
      } else {
        win.setTimeout(() => this.loadFuse(), IDLE_FALLBACK_MS);
      }
    });
  }

  /**
   * Starts loading the fuse.js chunk (idempotent). Until it lands — or if it never does
   * (offline) — `searchHits` serves substring matches, so search always works.
   */
  loadFuse(): void {
    if (this.fuseLoading || this.fuseCtor() !== null) return;
    this.fuseLoading = true;
    import('fuse.js').then(
      (m) => this.fuseCtor.set(m.default),
      () => {
        // Clear the latch so a later search attempt retries the load.
        this.fuseLoading = false;
      },
    );
  }

  setQuery(value: string): void {
    this.query.set(value);
    if (value) this.loadFuse();
  }

  selectCategory(id: TabId): void {
    // Searching ignores the category filter, so picking a tab is the natural way to leave search.
    this.query.set('');
    this.selectedCategoryId.set(id);
  }

  toggleLayout(): void {
    this.layout.update((l) => (l === 'list' ? 'grid' : 'list'));
  }

  retry(): void {
    this.menuRes.reload();
  }

  /** Fuse results come back sorted by score already. */
  private fuzzySearch(fuse: Fuse<SearchEntry>, query: string): MenuEntry[] {
    return fuse.search(query).map(({ item: entry, matches }: FuseResult<SearchEntry>) => {
      const nameRanges: HighlightRange[] = [];
      const descriptionRanges: HighlightRange[] = [];
      for (const match of matches ?? []) {
        const isName = match.key === 'name';
        const source = isName ? entry.name : entry.description;
        const target = isName ? nameRanges : descriptionRanges;
        for (const [start, end] of match.indices) {
          // Fuse index pairs are inclusive; toOriginalRange takes [start, end).
          const range = toOriginalRange(source, start, end + 1);
          if (range) target.push(range);
        }
      }
      return { item: entry.item, nameRanges, descriptionRanges };
    });
  }

  /** Exact-substring fallback over the same canonical texts: translit works, typos don't. */
  private substringSearch(query: string): MenuEntry[] {
    const hits: { entry: MenuEntry; rank: number }[] = [];
    for (const { item, name, description } of this.searchEntries()) {
      const inName = name.text.indexOf(query);
      const inDescription = description.text.indexOf(query);
      if (inName < 0 && inDescription < 0) continue;
      const nameRange = inName >= 0 ? toOriginalRange(name, inName, inName + query.length) : null;
      const descriptionRange =
        inDescription >= 0
          ? toOriginalRange(description, inDescription, inDescription + query.length)
          : null;
      hits.push({
        entry: {
          item,
          nameRanges: nameRange ? [nameRange] : NO_RANGES,
          descriptionRanges: descriptionRange ? [descriptionRange] : NO_RANGES,
        },
        rank: inName >= 0 ? inName : DESCRIPTION_RANK_OFFSET + inDescription,
      });
    }
    return hits.sort((a, b) => a.rank - b.rank).map((h) => h.entry);
  }

  /** MUST never error: every failure is folded into a RawState value below. If an error ever
   * escaped, menuRes would enter 'error' status and raw()'s value() read would start throwing. */
  private loadMenu() {
    const token = this.qrToken.value;
    if (!token) return of<RawState>({ kind: 'notFound' });

    return forkJoin({
      org: this.qr.getOrg(),
      menu: this.qr.getMenu(),
    }).pipe(
      map(({ org, menu }): RawState => ({ kind: 'loaded', org, menu })),
      // Connectivity/server failures (status 0 / 5xx) are retryable; a 4xx or an
      // empty envelope (unwrap throws) means the QR itself is wrong -> notFound.
      catchError((err: unknown) =>
        of<RawState>(
          err instanceof HttpErrorResponse && (err.status === 0 || err.status >= 500)
            ? { kind: 'error' }
            : { kind: 'notFound' },
        ),
      ),
    );
  }
}
